import {
  ImageProps,
  Slide,
  TextProps,
  TextPropsTextConfig,
} from '@features/powerpoint';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { dateRangeToString } from '@utils/dates';
import { contentSlideHeader, textStyles } from '@features/lci-report/utils';
import { calendar, measurement } from '@features/lci-report/images';
import {
  convertSlugToDimensionName,
  validDimensionSlug,
} from '@features/reports';

type DatesWithIcon = {
  header: {
    text: string;
    options: TextProps['options'];
  };
  dates: {
    text: string;
    options: TextProps['options'];
  };
  image: {
    path: string;
  };
};

export const TOP_BOTTOM_NAMES = ['Top', 'Bottom'] as const;

export const isTopBottomName = (
  name: string
): name is (typeof TOP_BOTTOM_NAMES)[number] => {
  return (TOP_BOTTOM_NAMES as readonly string[]).includes(name);
};

const fieldHeaderOptions: TextProps['options'] = {
  h: 0.27,
  w: 3.26,
  color: unifiedPlatformColorPalette.inmarketGreen,
  fontSize: 10,
  bold: true,
} as const;

const fieldValueOptions: TextProps['options'] = {
  color: textStyles.contentSlideDefaults?.color,
  h: 0.2,
  w: 3.26,
  fontSize: 14,
} as const;

const FONT_SIZE_THRESHOLD = 11;

const mapToTextProps = (
  sortedNameCount: [string, number][]
): TextPropsTextConfig[] =>
  sortedNameCount.map(([key, count]) => ({
    text: `${validDimensionSlug(key) ? convertSlugToDimensionName(key) : key} (${count})`,
    options: {
      color: textStyles.contentSlideDefaults?.color,
      breakLine: true,
      bullet: true,
      fontSize: sortedNameCount.length > FONT_SIZE_THRESHOLD ? 12 : 14,
    },
  }));

const getDimensionReport = (dimensionData: string[]): TextPropsTextConfig[] => {
  const nameCount: Record<string, number> = dimensionData.reduce(
    (acc, name) => {
      acc[name] = (acc[name] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const filteredNameCount = Object.entries(nameCount).filter(
    ([key]) => !isTopBottomName(key)
  );

  const sortedFilteredNameCount = filteredNameCount.toSorted(
    ([_, countA], [__, countB]) => countB - countA
  );

  return mapToTextProps(sortedFilteredNameCount);
};

const columnXOffsets = [0, 2.37, 5.82] as const;
const COLUMN_COUNT = columnXOffsets.length;
const ROW_Y_OFFSET = 0.95;
const VALUE_Y_OFFSET = 0.43;
const X_START = 0.84;
const Y_START = 3.18;

const calculateTextPosition = (index: number) => {
  const column = index % COLUMN_COUNT;
  const row = Math.floor(index / COLUMN_COUNT);
  const xOffset = columnXOffsets[column];
  const yOffset = row * ROW_Y_OFFSET;

  return {
    x: X_START + xOffset,
    y: Y_START + yOffset,
  };
};

const buildDatesWithIcon = (
  fieldHeader: string,
  dates: [string, string],
  image: string
): DatesWithIcon => {
  return {
    header: { text: fieldHeader, options: { ...fieldHeaderOptions } },
    dates: {
      text: dateRangeToString(dates),
      options: { ...fieldValueOptions },
    },
    image: { path: image },
  };
};

const buildMetrics = (fieldHeader: TextProps, formattedMetric: TextProps) => {
  return [
    { text: fieldHeader.text, options: fieldHeader.options },
    { text: formattedMetric.text, options: formattedMetric.options },
  ];
};

const buildListWithHeader = (
  fieldHeader: TextProps,
  formattedMetrics: TextProps
): TextProps[] => {
  return [
    { text: fieldHeader.text, options: fieldHeader.options },
    { text: formattedMetrics.text, options: formattedMetrics.options },
  ];
};

type OverviewSlideProps = {
  flightDates: [string, string];
  measurementDates: [string, string];
  metrics: { name: string; value: string }[];
  reportsIncluded: string[];
  reportingDimensions: string[];
  slideMaster: string;
};

export const overviewSlideInterface = (props: OverviewSlideProps): Slide => {
  const formattedReportingDimensions = getDimensionReport(
    props.reportingDimensions ?? []
  );

  const datesWithIcons: DatesWithIcon[] = [
    buildDatesWithIcon(
      'Flight Dates'.toUpperCase(),
      props.flightDates,
      calendar
    ),
    buildDatesWithIcon(
      'Measurement Period'.toUpperCase(),
      props.measurementDates,
      measurement
    ),
  ];

  const images: ImageProps[] =
    datesWithIcons.length > 0
      ? datesWithIcons.map((item, index) => ({
          path: item.image.path,
          h: 0.29,
          w: 0.29,
          x: index === 0 ? 0.96 : 4.96,
          y: 1.99,
        }))
      : [];

  const dateTexts: TextProps[] = datesWithIcons.flatMap((item, index) => [
    {
      text: item?.header?.text?.toUpperCase() || '',
      options: {
        ...fieldHeaderOptions,
        x: index === 0 ? 1.47 : 5.49,
        y: index === 0 ? 1.78 : 1.75,
      },
    },
    {
      text: item?.dates?.text || '',
      options: {
        ...fieldValueOptions,
        x: index === 0 ? 1.47 : 5.49,
        y: index === 0 ? 2.2 : 2.19,
      },
    },
  ]);

  const metrics = props.metrics.map((metric, index) => {
    const { x, y } = calculateTextPosition(index);

    return {
      name: metric.name.toUpperCase(),
      value: metric.value,
      headerOptions: {
        ...fieldHeaderOptions,
        x,
        y,
      },
      valueOptions: {
        ...fieldValueOptions,
        x,
        y: y + VALUE_Y_OFFSET,
      },
    };
  });

  const textMetrics: TextProps[] = metrics.flatMap(metric => {
    return buildMetrics(
      {
        text: metric.name,
        options: metric.headerOptions,
      },
      {
        text: metric.value,
        options: metric.valueOptions,
      }
    );
  });

  const reportsIncluded: TextPropsTextConfig[] = (
    props?.reportsIncluded ?? []
  ).map(metric => ({
    text: metric,
    options: {
      color: textStyles.contentSlideDefaults?.color,
      breakLine: true,
      bullet: true,
      fontSize: 14,
    },
  }));

  const reportsIncludedPosition = calculateTextPosition(props.metrics.length);

  const textLists: TextProps[] = [
    ...buildListWithHeader(
      {
        text: 'Reports Included'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
          x: reportsIncludedPosition.x,
          y: reportsIncludedPosition.y,
        },
      },
      {
        text: reportsIncluded,
        options: {
          h: 1.51,
          w: 3.26,
          x: reportsIncludedPosition.x,
          y: reportsIncludedPosition.y + VALUE_Y_OFFSET,
          valign: 'top',
          paraSpaceAfter: 8,
        },
      }
    ),
    ...buildListWithHeader(
      {
        text: 'Reporting Dimensions'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
          x: 9.92,
          y: 3.18,
        },
      },
      {
        text: formattedReportingDimensions,
        options: {
          h: 1.51,
          w: 3.26,
          x: 9.92,
          y: 3.6,
          valign: 'top',
          paraSpaceAfter: 8,
        },
      }
    ),
  ];

  return {
    slideMaster: props.slideMaster,
    images,
    shapes: [
      {
        shapeName: 'ellipse',
        options: {
          h: 0.53,
          w: 0.53,
          x: 0.84,
          y: 1.87,
          fill: {
            color: 'FFFFFF',
          },
          line: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            width: 1,
          },
        },
      },
      {
        shapeName: 'ellipse',
        options: {
          h: 0.53,
          w: 0.53,
          x: 4.84,
          y: 1.87,
          fill: {
            color: 'FFFFFF',
          },
          line: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            width: 1,
          },
        },
      },
      {
        shapeName: 'rect',
        options: {
          h: 0,
          w: 11.62,
          x: 0.84,
          y: 2.76,
          line: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            width: 2.25,
          },
        },
      },
    ],
    texts: [
      contentSlideHeader('Campaign Overview'),
      ...dateTexts,
      ...textMetrics,
      ...textLists,
    ],
  };
};
